/**
 * AuditFeed — /audit route (admin, editor, auditor).
 * Filters by entity, action, actor email, and rolling window; exports the
 * current filter as CSV via `GET /api/audit-logs/export.csv`.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Download, ScrollText, RefreshCw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';

const ENTITY_OPTIONS = ['', 'supplier', 'supplier_submission', 'organization', 'product', 'evidence'];
const ACTION_OPTIONS = ['', 'invited', 'accepted_invitation', 'suspended', 'verified',
  'submitted', 'approved', 'rejected'];
const DAY_OPTIONS = [7, 30, 90, 180, 365];

export default function AuditFeed() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({ entity: '', action: '', actor_email: '', days: 30 });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.entity) params.set('entity', filters.entity);
      if (filters.action) params.set('action', filters.action);
      if (filters.actor_email) params.set('actor_email', filters.actor_email);
      if (filters.days) params.set('days', String(filters.days));
      params.set('limit', '200');
      const r = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(r.data?.logs || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load audit feed');
    } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.entity) params.set('entity', filters.entity);
      if (filters.action) params.set('action', filters.action);
      if (filters.actor_email) params.set('actor_email', filters.actor_email);
      params.set('days', String(filters.days || 30));
      const r = await api.get(`/audit-logs/export.csv?${params.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `audit_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4" data-testid="audit-feed">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-2">
            <ScrollText className="w-6 h-6" /> Audit feed
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Every mutation across your workspace, in chronological order.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={fetchLogs} data-testid="audit-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={exportCsv} disabled={exporting} data-testid="audit-export-btn">
            {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
            Export CSV
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Select label="Entity" testid="audit-filter-entity" value={filters.entity}
                  onChange={(v) => setFilters({ ...filters, entity: v })}
                  options={ENTITY_OPTIONS.map((v) => ({ value: v, label: v || 'All entities' }))} />
          <Select label="Action" testid="audit-filter-action" value={filters.action}
                  onChange={(v) => setFilters({ ...filters, action: v })}
                  options={ACTION_OPTIONS.map((v) => ({ value: v, label: v || 'All actions' }))} />
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground uppercase tracking-wide">Actor email</label>
            <Input placeholder="e.g. admin@veridex-one.com"
                   value={filters.actor_email} data-testid="audit-filter-actor"
                   onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })} />
          </div>
          <Select label="Window" testid="audit-filter-days" value={String(filters.days)}
                  onChange={(v) => setFilters({ ...filters, days: parseInt(v, 10) })}
                  options={DAY_OPTIONS.map((d) => ({ value: String(d), label: `Last ${d} days` }))} />
        </div>
      </Card>

      <Card>
        <div className="px-4 py-2 border-b border-border text-xs text-muted-foreground flex items-center justify-between">
          <span>Showing <strong className="text-foreground">{logs.length}</strong> of <strong className="text-foreground">{total}</strong></span>
        </div>
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : logs.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="audit-empty">
            No events match these filters.
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="audit-list">
            {logs.map((log) => (
              <li key={log.id} className="px-4 py-3 hover:bg-muted/30 transition-colors" data-testid={`audit-row-${log.id}`}>
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wide">{log.entity}</Badge>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm">
                      <span className="font-medium">{log.action}</span>
                      <span className="text-muted-foreground"> · {log.actor_email || log.actor_id}</span>
                      <span className="text-muted-foreground"> ({log.actor_role})</span>
                    </div>
                    {log.metadata && Object.keys(log.metadata).length > 0 && (
                      <div className="text-xs text-muted-foreground mt-1 font-mono truncate max-w-full">
                        {JSON.stringify(log.metadata).slice(0, 180)}{JSON.stringify(log.metadata).length > 180 ? '…' : ''}
                      </div>
                    )}
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {log.entity_id && <span>id {String(log.entity_id).slice(0, 12)}…</span>}
                      {log.ip_address && <span> · {log.ip_address}</span>}
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground shrink-0">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function Select({ label, testid, value, onChange, options }) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground uppercase tracking-wide">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
